import React, { useEffect, useState } from "react";
import { Alert, Descriptions, InputNumber, message, Radio, Row, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { Button } from "Components/General/Button";
import { documentsTableStore } from "../Stores/DocumentsTableStore";

export interface ChangePositionModalProps extends ModalComponentProps {
  initialData: API_FileDataEntity | API_FolderDataEntity;
}
type DocTypeName = "Folder" | "File";
interface DocTypeProps {
  name: DocTypeName;
  path: string;
  prop: keyof Pick<API_PositionChangeRequest, "fileId" | "directoryId">;
}

export const ChangePositionModal: React.FC<ChangePositionModalProps> = ({ initialData, hideModal }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const placeholder: API_PositionChangeRequest = {
    newPosition: null,
    directoryId: null,
    fileId: null,
    strategy: null,
  };
  const [formData, setFormData] = useState<API_PositionChangeRequest>(placeholder);
  const [loading, setLoading] = useState(false);
  const [docTypeProps, setDocTypeProps] = useState<DocTypeProps>(null);
  const [optionsVisible, setOptionsVisible] = useState(false);

  const onChange = (value: API_POSITIONCHANGEREQUEST_STRATEGY | number, key: keyof API_PositionChangeRequest) => {
    const data: API_PositionChangeRequest = { [key]: value };
    if (typeof value === "number" && value !== formData.newPosition) {
      data.strategy = null;
      setOptionsVisible(false);
    }
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const onCancel = () => {
    hideModal();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      let response: API_BooleanData = { booleanValue: true };
      if (!optionsVisible) {
        response = await documentsTableStore.isFreeDocumentPosition(formData, initialData.parentId);
      }
      if (response.booleanValue) {
        await documentsTableStore.changeDocumentPosition(formData, initialData.parentId);
        setOptionsVisible(false);
        message.success(t("Success"));
        hideModal();
      } else {
        setOptionsVisible(true);
        onChange("SWAP", "strategy");
      }
    } catch (error) {
      message.error(t("Error"));
    } finally {
      setLoading(false);
    }
  };

  const renderInitialData = (initialData: ChangePositionModalProps["initialData"]) => {
    return (
      <Descriptions bordered column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
        <Descriptions.Item label={t("Type")}>{docTypeProps?.name}</Descriptions.Item>
        <Descriptions.Item label={t("Path")}>
          <Typography.Text>{docTypeProps?.path}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t("Current position")}>{initialData?.position}</Descriptions.Item>
        <Descriptions.Item label={t("New position")}>
          <InputNumber
            style={{ width: "100%" }}
            value={formData.newPosition}
            autoFocus={true}
            onChange={(value) => onChange(value, "newPosition")}
          />
        </Descriptions.Item>
      </Descriptions>
    );
  };

  const renderOptionsForm = () => {
    const options: {
      label: string;
      value: API_POSITIONCHANGEREQUEST_STRATEGY;
    }[] = [
      {
        value: "SWAP",
        label: "Swap the two elements",
      },
      {
        value: "RENUMBER_FOLLOWING",
        label: "Add this element there and renumber the following elements in ascending order",
      },
    ];
    return (
      <div className="mt-1">
        <Radio.Group onChange={({ target: { value } }) => onChange(value, "strategy")} value={formData.strategy}>
          <Space orientation="vertical">
            {options.map(({ label, value }) => (
              <Radio key={value} value={value}>
                {t(label)}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>
    );
  };

  useEffect(() => {
    const docProps: DocTypeProps = initialData.type.startsWith("FO")
      ? { prop: "directoryId", name: "Folder", path: `${initialData.name}/` }
      : { prop: "fileId", name: "File", path: (initialData as API_FileDataEntity).filepath };

    onChange(initialData.id, docProps.prop);
    setDocTypeProps(docProps);
  }, []);

  return (
    <div>
      {renderInitialData(initialData)}
      {optionsVisible && (
        <Alert
          title={t("The selected position is already in use by another element")}
          description={renderOptionsForm()}
          type="warning"
          showIcon
          className="mt-2"
        />
      )}
      <Row justify="space-between" className="mt-2">
        <Button onClick={onCancel} type="default">
          {t("Cancel")}
        </Button>
        <Button onClick={onFinish} type="primary" loading={loading}>
          {t("Save")}
        </Button>
      </Row>
    </div>
  );
};
